import React, { useState, useEffect } from 'react';
import WatconnectApi from '../../../api/WatconnectApi';
import DataApi from '../../../api/DataApi';
import './WhatsAppMessenger.css';

const TemplateModal = ({
  isOpen,
  onClose,
  recipientPhone,
  recipientName,
  selectedWhatsAppSetting,
  onSendComplete,
}) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [parameterValues, setParameterValues] = useState({});
  const [templateChangeKey, setTemplateChangeKey] = useState(0);

  const categories = ['ALL', 'MARKETING', 'UTILITY'];

  // File validation configurations
  // const FILE_VALIDATIONS = {
  //   DOCUMENT: {
  //     maxSize: 100 * 1024 * 1024,
  //     allowedTypes: [
  //       'text/plain',
  //       'application/vnd.ms-excel',
  //       'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  //       'application/msword',
  //       'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  //       'application/vnd.ms-powerpoint',
  //       'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  //       'application/pdf',
  //     ],
  //     allowedExtensions: [
  //       '.txt',
  //       '.xls',
  //       '.xlsx',
  //       '.doc',
  //       '.docx',
  //       '.ppt',
  //       '.pptx',
  //       '.pdf',
  //     ],
  //   },
  //   IMAGE: {
  //     maxSize: 5 * 1024 * 1024,
  //     allowedTypes: ['image/jpeg', 'image/png'],
  //     allowedExtensions: ['.jpeg', '.jpg', '.png'],
  //   },
  //   VIDEO: {
  //     maxSize: 16 * 1024 * 1024,
  //     allowedTypes: ['video/3gpp', 'video/mp4'],
  //     allowedExtensions: ['.3gp', '.mp4'],
  //   },
  // };
  const transformTemplate = template => {
    console.log('template', template);
    const components = [];

    const safeText = val => {
      if (val === null || val === 'null' || val === undefined) return '';
      if (typeof val === 'object') return val.text || JSON.stringify(val);
      return String(val || '');
    };

    const extractExampleValues = (text, exampleData) => {
      if (!text) return [];
      const regex = /\{\{(\d+)\}\}/g;
      const matches = [];
      let match;
      let paramIndexes = [];

      // First, collect all parameter indexes in order
      while ((match = regex.exec(text))) {
        paramIndexes.push(parseInt(match[1]));
      }

      // Then assign example values based on parameter indexes
      paramIndexes.forEach((paramIndex, arrayIndex) => {
        // Use provided example data if available, otherwise use generic placeholder
        if (exampleData && exampleData[arrayIndex] !== undefined) {
          matches.push(exampleData[arrayIndex]);
        } else {
          matches.push(`value${paramIndex}`);
        }
      });

      return matches;
    };

    // 🖼️ HEADER — handle image/video/document or plain text header
    if (template.header && template.header !== 'null') {
      const headerComponent = {
        type: 'HEADER',
        format: template.header.toUpperCase(),
      };

      // If image/video/document header => use file_data
      if (
        ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(
          template.header.toUpperCase()
        ) &&
        template.file_data
      ) {
        headerComponent.file = {
          url: template.file_data.file_url || '',
          name: template.file_data.file_name || '',
          mime_type: template.file_data.mime_type || '',
        };
      } else if (template.header_body && template.header_body !== 'null') {
        headerComponent.text = safeText(template.header_body);
      }

      components.push(headerComponent);
    }

    if (template.message_body) {
      // Use stored example_body_text from database
      let exampleValues = [];
      if (
        template.example_body_text &&
        Array.isArray(template.example_body_text)
      ) {
        exampleValues = template.example_body_text;
      }

      components.push({
        type: 'BODY',
        text: safeText(template.message_body),
        example: {
          body_text: [
            extractExampleValues(template.message_body, exampleValues),
          ],
        },
      });
    }

    // 🦶 FOOTER
    if (template.footer && template.footer !== 'null') {
      components.push({
        type: 'FOOTER',
        text: safeText(template.footer),
      });
    }

    // 🔘 BUTTONS (normal templates only)
    if (Array.isArray(template.buttons) && template.buttons.length > 0) {
      components.push({
        type: 'BUTTONS',
        buttons: template.buttons.map(btn => ({
          type: btn.type,
          text: btn.text,
          url: btn.url || undefined,
          phone_number: btn.phone_number || undefined,
        })),
      });
    }

    // 🎠 CAROUSEL templates - FIXED VERSION
    if (Array.isArray(template.cards) && template.cards.length > 0) {
      const carouselCards = template.cards.map(card => {
        const cardComponents = [];

        // Header (image or video)
        if (card.header) {
          cardComponents.push({
            type: 'HEADER',
            format: card.header.format?.toUpperCase() || 'IMAGE',
            handle: card.header.handle || '',
          });
        }

        // Body with proper example values
        if (card.body) {
          const bodyText = safeText(card.body.text);
          let exampleValues = [];

          // Extract example values from card.body.example if available
          if (card.body.example && Array.isArray(card.body.example)) {
            exampleValues = card.body.example;
          }

          cardComponents.push({
            type: 'BODY',
            text: bodyText,
            example: {
              body_text: [extractExampleValues(bodyText, exampleValues)],
            },
          });
        }

        // Buttons (CTA / Quick replies)
        if (Array.isArray(card.buttons) && card.buttons.length > 0) {
          cardComponents.push({
            type: 'BUTTONS',
            buttons: card.buttons.map(btn => ({
              type: btn.type,
              text: btn.text,
              url: btn.url || '',
              phone_number: btn.phone_number || '',
            })),
          });
        }

        return { components: cardComponents };
      });

      components.push({
        type: 'CAROUSEL',
        cards: carouselCards,
      });
    }

    // 🧾 Return final unified structure
    return {
      id: template.id,
      name: template.template_name,
      category: template.category,
      language: template.language,
      status: template.status,
      template_type: template.template_type,
      components,
      // Keep original file_data for reference
      file_data: template.file_data,
      example_body_text: template.example_body_text,
    };
  };
  // Extract example values from template text with proper parameter indexing
  const extractExampleValues = (text, exampleData = []) => {
    if (!text) return [];
    const regex = /\{\{(\d+)\}\}/g;
    const matches = [];
    let match;

    while ((match = regex.exec(text))) {
      const paramIndex = parseInt(match[1]);
      // Use provided example data if available, otherwise use generic placeholder
      if (exampleData && exampleData[paramIndex - 1]) {
        matches.push(exampleData[paramIndex - 1]);
      } else {
        matches.push(`value${paramIndex}`);
      }
    }
    return matches;
  };

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedCategory === 'ALL') {
      setFilteredTemplates(templates);
    } else {
      setFilteredTemplates(
        templates.filter(template => template.category === selectedCategory)
      );
    }

    setSelectedTemplate(null);
    setParameterValues({});
    // setFileUploads({});
    // setUploadedFilePreviews({});
    // setFileErrors({});
  }, [templates, selectedCategory]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const dataApi = new DataApi('watconnect');
      const res = await dataApi.fetchById(
        `watconnect_template?business_number=${selectedWhatsAppSetting}`
      );

      if (res?.data?.data && Array.isArray(res.data?.data)) {
        console.log('Raw templates:', res.data?.data);

        // Transform templates and filter only APPROVED ones
        const transformedTemplates = res.data.data
          .filter(t => t.status === 'APPROVED')
          .map(transformTemplate);

        console.log('Transformed templates:', transformedTemplates);
        setTemplates(transformedTemplates);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // const validateFile = (file, format) => {
  //   const validation =
  //     FILE_VALIDATIONS[
  //       format === 'IMAGE' ? 'IMAGE' : format === 'VIDEO' ? 'VIDEO' : 'DOCUMENT'
  //     ];

  //   if (!validation) {
  //     return { isValid: false, error: `Unsupported format: ${format}` };
  //   }

  //   if (file.size > validation.maxSize) {
  //     const maxSizeMB = validation.maxSize / (1024 * 1024);
  //     return {
  //       isValid: false,
  //       error: `File size too large. Maximum allowed: ${maxSizeMB}MB`,
  //     };
  //   }

  //   if (!validation.allowedTypes.includes(file.type)) {
  //     return {
  //       isValid: false,
  //       error: `Invalid file type. Allowed types: ${validation.allowedExtensions.join(', ')}`,
  //     };
  //   }

  //   const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
  //   if (!validation.allowedExtensions.includes(fileExtension)) {
  //     return {
  //       isValid: false,
  //       error: `Invalid file extension. Allowed: ${validation.allowedExtensions.join(', ')}`,
  //     };
  //   }

  //   return { isValid: true, error: '' };
  // };

  const handleTemplateSelect = template => {
    if (!template) {
      setSelectedTemplate(null);
      setParameterValues({});
      setTemplateChangeKey(prev => prev + 1);
      return;
    }

    setSelectedTemplate(template);
    setParameterValues({});
    setTemplateChangeKey(prev => prev + 1);

    if (recipientName) {
      const initialParams = {};
      template.components?.forEach(component => {
        if (component.type === 'BODY' && component.example?.body_text?.[0]) {
          const exampleValues = component.example.body_text[0];
          const params = extractParameters(component.text);

          params.forEach((paramIndex, arrayIndex) => {
            if (exampleValues[arrayIndex]?.toLowerCase().includes('name')) {
              initialParams[`body_${paramIndex}`] = recipientName;
            }
          });
        }
      });

      template.components?.forEach(component => {
        if (component.type === 'CAROUSEL') {
          component.cards?.forEach((card, cardIndex) => {
            card.components?.forEach(cardComp => {
              if (
                cardComp.type === 'BODY' &&
                cardComp.example?.body_text?.[0]
              ) {
                const exampleValues = cardComp.example.body_text[0];
                const params = extractParameters(cardComp.text);

                params.forEach((paramIndex, arrayIndex) => {
                  if (
                    exampleValues[arrayIndex]?.toLowerCase().includes('name')
                  ) {
                    initialParams[`carousel_${cardIndex}_${paramIndex}`] =
                      recipientName;
                  }
                });
              }
            });
          });
        }
      });

      setParameterValues(initialParams);
    }
  };

  const handleParameterChange = (paramIndex, value) => {
    setParameterValues(prev => ({
      ...prev,
      [paramIndex]: value,
    }));
  };

  const extractParameters = text => {
    if (!text) return [];
    const paramRegex = /\{\{(\d+)\}\}/g;
    const params = [];
    let match;
    while ((match = paramRegex.exec(text)) !== null) {
      params.push(parseInt(match[1]));
    }
    return [...new Set(params)].sort((a, b) => a - b);
  };

  const areAllParametersFilled = () => {
    if (!selectedTemplate) return false;

    // Check body parameters
    const bodyComponents =
      selectedTemplate.components?.filter(comp => comp.type === 'BODY') || [];
    for (const component of bodyComponents) {
      const params = extractParameters(component.text);
      for (const param of params) {
        if (!parameterValues[`body_${param}`]?.trim()) {
          return false;
        }
      }
    }

    // Check carousel body parameters
    const carouselComponents =
      selectedTemplate.components?.filter(comp => comp.type === 'CAROUSEL') ||
      [];
    for (const carousel of carouselComponents) {
      for (
        let cardIndex = 0;
        cardIndex < (carousel.cards?.length || 0);
        cardIndex++
      ) {
        const card = carousel.cards[cardIndex];
        for (const cardComp of card.components || []) {
          if (cardComp.type === 'BODY') {
            const params = extractParameters(cardComp.text);
            for (const param of params) {
              if (!parameterValues[`carousel_${cardIndex}_${param}`]?.trim()) {
                return false;
              }
            }
          }
        }
      }
    }

    return true;
  };
  const renderParameterInputs = () => {
    if (!selectedTemplate) return null;

    const inputs = [];

    // Body parameters only
    const bodyComponents =
      selectedTemplate.components?.filter(comp => comp.type === 'BODY') || [];
    bodyComponents.forEach((component, compIndex) => {
      const params = extractParameters(component.text);
      const exampleValues = component.example?.body_text?.[0] || [];

      params.forEach((paramIndex, arrayIndex) => {
        const example = exampleValues[arrayIndex] || `value${paramIndex}`;
        inputs.push(
          <div key={`body-param-${paramIndex}`} className='parameter-input'>
            <label>
              Body Parameter {paramIndex}{' '}
              <span className='example-text'>(e.g., {example})</span>
            </label>
            <input
              type='text'
              value={parameterValues[`body_${paramIndex}`] || ''}
              onChange={e =>
                handleParameterChange(`body_${paramIndex}`, e.target.value)
              }
              placeholder={`Enter value for parameter ${paramIndex}`}
              className='form-control'
            />
          </div>
        );
      });
    });

    // Carousel body parameters only
    const carouselComponents =
      selectedTemplate.components?.filter(comp => comp.type === 'CAROUSEL') ||
      [];
    carouselComponents.forEach(carousel => {
      carousel.cards?.forEach((card, cardIndex) => {
        inputs.push(
          <div
            key={`carousel-${cardIndex}-header`}
            className='carousel-section-header'
          >
            <h6>Carousel Card {cardIndex + 1}</h6>
          </div>
        );

        card.components?.forEach(cardComp => {
          if (cardComp.type === 'BODY') {
            const params = extractParameters(cardComp.text);
            const exampleValues = cardComp.example?.body_text?.[0] || [];

            params.forEach((paramIndex, arrayIndex) => {
              const example = exampleValues[arrayIndex] || `value${paramIndex}`;
              inputs.push(
                <div
                  key={`carousel-${cardIndex}-param-${paramIndex}`}
                  className='parameter-input'
                >
                  <label>
                    Card {cardIndex + 1} - Parameter {paramIndex}{' '}
                    <span className='example-text'>(e.g., {example})</span>
                  </label>
                  <input
                    type='text'
                    value={
                      parameterValues[`carousel_${cardIndex}_${paramIndex}`] ||
                      ''
                    }
                    onChange={e =>
                      handleParameterChange(
                        `carousel_${cardIndex}_${paramIndex}`,
                        e.target.value
                      )
                    }
                    placeholder={`Enter value for card ${cardIndex + 1} parameter ${paramIndex}`}
                    className='form-control'
                  />
                </div>
              );
            });
          }
        });
      });
    });

    return inputs;
  };
  const renderWhatsAppPreview = () => {
    if (!selectedTemplate) return null;

    // Debug: Check what's in file_data
    console.log('Full selectedTemplate:', selectedTemplate);
    console.log('File data:', selectedTemplate.file_data);
    console.log('File URL:', selectedTemplate.file_data?.file_url);

    return (
      <div className='whatsapp-preview'>
        <div className='template-message'>
          {selectedTemplate.components?.map((component, index) => {
            if (component.type === 'HEADER') {
              if (
                component.format === 'IMAGE' ||
                component.format === 'VIDEO' ||
                component.format === 'DOCUMENT' // Added DOCUMENT here
              ) {
                const fileUrl = selectedTemplate.file_data?.file_url;

                console.log('Using file URL:', fileUrl); // Debug

                return (
                  <div key={index} className='template-header'>
                    {fileUrl ? (
                      <div className='existing-file-preview'>
                        {component.format === 'IMAGE' ? (
                          <img
                            src={fileUrl}
                            alt='Template header'
                            className='template-header-image'
                            onError={e => {
                              console.error(
                                'Image failed to load:',
                                fileUrl,
                                e
                              );
                              e.target.style.display = 'none';
                              const fallback = e.target.nextSibling;
                              if (fallback) fallback.style.display = 'block';
                            }}
                            onLoad={() =>
                              console.log('Image loaded successfully:', fileUrl)
                            }
                          />
                        ) : component.format === 'VIDEO' ? (
                          <video
                            controls
                            className='template-header-video'
                            onError={e => {
                              console.error(
                                'Video failed to load:',
                                fileUrl,
                                e
                              );
                              e.target.style.display = 'none';
                              const fallback = e.target.nextSibling;
                              if (fallback) fallback.style.display = 'block';
                            }}
                          >
                            <source
                              src={fileUrl}
                              type={selectedTemplate.file_data.mime_type}
                            />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          // DOCUMENT preview
                          <div className='document-preview'>
                            <div className='document-icon'>📄</div>
                            <div className='document-info'>
                              <div className='document-name'>
                                {selectedTemplate.file_data?.file_name ||
                                  'Document'}
                              </div>
                              <div className='document-size'>
                                {selectedTemplate.file_data?.file_size
                                  ? `(${(selectedTemplate.file_data.file_size / 1024).toFixed(1)} KB)`
                                  : '(Document)'}
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Fallback if media fails to load */}
                        <div
                          className='media-preview-placeholder'
                          style={{ display: 'none' }}
                        >
                          {component.format === 'IMAGE'
                            ? '🖼️'
                            : component.format === 'VIDEO'
                              ? '🎥'
                              : '📄'}{' '}
                          Media
                        </div>
                      </div>
                    ) : (
                      <div className='media-preview-placeholder'>
                        {component.format === 'IMAGE'
                          ? '🖼️ Image Header'
                          : component.format === 'VIDEO'
                            ? '🎥 Video Header'
                            : '📄 Document Header'}
                      </div>
                    )}
                  </div>
                );
              } else if (component.text) {
                return (
                  <div key={index} className='template-header'>
                    <div className='template-header-text text-dark fw-semibold'>
                      {typeof component.text === 'string'
                        ? component.text
                        : JSON.stringify(component.text)}
                    </div>
                  </div>
                );
              }
            }
            return null;
          })}

          {/* Rest of the BODY, CAROUSEL, FOOTER, and BUTTONS rendering remains the same */}
          {selectedTemplate.components?.map((component, index) => {
            if (component.type === 'BODY') {
              let processedText = component.text;
              extractParameters(component.text).forEach(param => {
                const value = parameterValues[`body_${param}`];
                if (value) {
                  processedText = processedText.replace(
                    new RegExp(`\\{\\{${param}\\}\\}`, 'g'),
                    value
                  );
                } else {
                  processedText = processedText.replace(
                    new RegExp(`\\{\\{${param}\\}\\}`, 'g'),
                    `{{${param}}}`
                  );
                }
              });
              return (
                <div key={index} className='template-body'>
                  <div>
                    {processedText
                      .split(/(\{\{\d+\}\})/)
                      .map((part, i) =>
                        part.match(/\{\{\d+\}\}/) ? <b key={i}>{part}</b> : part
                      )}
                  </div>
                </div>
              );
            }
            return null;
          })}

          {selectedTemplate.components?.map((component, index) => {
            if (component.type === 'CAROUSEL') {
              return (
                <div key={index} className='template-carousel'>
                  <div className='carousel-container'>
                    {component.cards?.map((card, cardIndex) => (
                      <div key={cardIndex} className='carousel-card'>
                        {card.components?.map((cardComp, compIndex) => {
                          if (
                            cardComp.type === 'HEADER' &&
                            (cardComp.format === 'IMAGE' ||
                              cardComp.format === 'VIDEO' ||
                              cardComp.format === 'DOCUMENT') // Added DOCUMENT here too
                          ) {
                            // For carousel cards, check if there's file data available
                            const hasFileData =
                              cardComp.file?.url ||
                              selectedTemplate.file_data?.file_url;

                            return (
                              <div key={compIndex} className='carousel-header'>
                                {hasFileData ? (
                                  <div className='existing-file-preview'>
                                    {cardComp.format === 'IMAGE' ? (
                                      <img
                                        src={
                                          cardComp.file?.url ||
                                          selectedTemplate.file_data.file_url
                                        }
                                        alt={`Card ${cardIndex + 1}`}
                                        className='carousel-header-image'
                                        onError={e => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display =
                                            'block';
                                        }}
                                      />
                                    ) : cardComp.format === 'VIDEO' ? (
                                      <video
                                        controls
                                        className='carousel-header-video'
                                        onError={e => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display =
                                            'block';
                                        }}
                                      >
                                        <source
                                          src={
                                            cardComp.file?.url ||
                                            selectedTemplate.file_data.file_url
                                          }
                                          type={
                                            cardComp.file?.mime_type ||
                                            selectedTemplate.file_data.mime_type
                                          }
                                        />
                                        Your browser does not support the video
                                        tag.
                                      </video>
                                    ) : (
                                      // DOCUMENT preview for carousel
                                      <div className='document-preview'>
                                        <div className='document-icon'>📄</div>
                                        <div className='document-info'>
                                          <div className='document-name'>
                                            {selectedTemplate.file_data
                                              ?.file_name || 'Document'}
                                          </div>
                                          <div className='document-size'>
                                            {selectedTemplate.file_data
                                              ?.file_size
                                              ? `(${(selectedTemplate.file_data.file_size / 1024).toFixed(1)} KB)`
                                              : '(Document)'}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    {/* Fallback if media fails to load */}
                                    <div
                                      className='media-preview-placeholder'
                                      style={{ display: 'none' }}
                                    >
                                      {cardComp.format === 'IMAGE'
                                        ? '🖼️'
                                        : cardComp.format === 'VIDEO'
                                          ? '🎥'
                                          : '📄'}{' '}
                                      Media
                                    </div>
                                  </div>
                                ) : (
                                  <div className='media-preview-placeholder'>
                                    {cardComp.format === 'IMAGE'
                                      ? '🖼️'
                                      : cardComp.format === 'VIDEO'
                                        ? '🎥'
                                        : '📄'}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })}

                        {card.components?.map((cardComp, compIndex) => {
                          if (cardComp.type === 'BODY') {
                            let processedText = cardComp.text;
                            extractParameters(cardComp.text).forEach(param => {
                              const value =
                                parameterValues[
                                  `carousel_${cardIndex}_${param}`
                                ];
                              if (value) {
                                processedText = processedText.replace(
                                  new RegExp(`\\{\\{${param}\\}\\}`, 'g'),
                                  value
                                );
                              } else {
                                processedText = processedText.replace(
                                  new RegExp(`\\{\\{${param}\\}\\}`, 'g'),
                                  `{{${param}}}`
                                );
                              }
                            });
                            return (
                              <div key={compIndex} className='carousel-body'>
                                {typeof processedText === 'string'
                                  ? processedText
                                  : JSON.stringify(processedText)}
                              </div>
                            );
                          }
                          return null;
                        })}

                        {card.components?.map((cardComp, compIndex) => {
                          if (cardComp.type === 'BUTTONS') {
                            return (
                              <div key={compIndex} className='carousel-buttons'>
                                {cardComp.buttons?.map((btn, btnIdx) => (
                                  <button
                                    key={btnIdx}
                                    className='carousel-button'
                                    disabled
                                  >
                                    {btn.text}
                                  </button>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })}

          {selectedTemplate.components?.map((component, index) => {
            if (component.type === 'FOOTER') {
              return (
                <div key={index} className='template-body text-muted'>
                  {typeof component.text === 'string'
                    ? component.text
                    : JSON.stringify(component.text)}
                </div>
              );
            }
            return null;
          })}

          {selectedTemplate.components?.map((component, index) => {
            if (component.type === 'BUTTONS') {
              return (
                <div key={index} className='template-buttons'>
                  {component.buttons?.map((button, btnIndex) => {
                    let buttonClass = 'template-button';
                    if (button.type === 'PHONE_NUMBER') buttonClass += ' phone';
                    else if (button.type === 'URL') buttonClass += ' url';
                    else buttonClass += ' primary';

                    return (
                      <button key={btnIndex} className={buttonClass} disabled>
                        {button.text}
                      </button>
                    );
                  })}
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  };
  const handleSendTemplate = async () => {
    if (!selectedTemplate || !areAllParametersFilled()) {
      alert(
        'Please fill all required parameters and fix any file upload errors'
      );
      return;
    }

    onSendComplete?.({
      selectedTemplate,
      selectedWhatsAppSetting,
      parameterValues,
    });

    setSending(true);
    // onClose();
  };

  if (!isOpen) return null;

  return (
    <div className='wa-modal-overlay'>
      <div className='wa-modal template-modal clean-modal'>
        <div className='wa-file-preview-header'>
          <div className='fw-bold fa-2x'>Send Template</div>
          <button className='wa-add-more-btn' onClick={onClose} title='Close'>
            ✕
          </button>
        </div>
        <div className='wa-modal-body scrollable'>
          <div className='modal-inner-content'>
            {loading ? (
              <div className='loading-state'>
                <p>Loading templates...</p>
              </div>
            ) : (
              <>
                <div className='template-section'>
                  <label className='form-label'>Select Category:</label>
                  <div className='category-buttons'>
                    {categories.map(category => (
                      <button
                        key={category}
                        className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>

                <div className='template-section'>
                  <label className='form-label'>Select Template:</label>
                  <select
                    className='form-select'
                    value={selectedTemplate?.id || ''}
                    onChange={e => {
                      const selected = filteredTemplates.find(
                        t => t.id === e.target.value
                      );
                      handleTemplateSelect(selected || null);
                    }}
                  >
                    <option value=''>-- Choose Template --</option>
                    {filteredTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.category})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedTemplate && (
                  <div className='template-content'>
                    <div className='parameter-box'>
                      <h6>Template Parameters</h6>
                      <div className='parameters-container'>
                        {renderParameterInputs()}
                        {/* {renderFileUploads()} */}
                      </div>
                      {!areAllParametersFilled() && (
                        <div className='parameter-warning'>
                          ⚠️ Please fill all required parameters and uploads.
                        </div>
                      )}
                    </div>

                    <div className='preview-box'>
                      <h6>WhatsApp Preview</h6>
                      {renderWhatsAppPreview()}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className='wa-modal-footer'>
          <button className='btn btn-secondary' onClick={onClose}>
            Cancel
          </button>
          <button
            className='btn btn-primary'
            onClick={handleSendTemplate}
            disabled={sending || !selectedTemplate || !areAllParametersFilled()}
          >
            {sending ? (
              <>
                <span className='loading-spinner'></span>
                Sending...
              </>
            ) : (
              'Send Template'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;
